import React from 'react';
import { TextField as StyledTextField } from '@davies-group/white-label-ui/ui'
import { Button, CircularProgress, InputAdornment } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import IOption from '../../models/IOption';
import { IValidator, IValidatorResult } from '../../models/IValidator';

import InputBase from './InputBase';

interface IProps
{
	value: IOption | null,
	options: IOption[],
	onValueChange(value: IOption | null): void,
	onInputChange(value: string): void,

	isLoading: boolean,
	validator?: IValidator,
	onValidatorResultChanged?(validatorResult: IValidatorResult): void,
	answerValue?: unknown[],
	outerLabel?: string,
	helpText?: string,
	onLoadMore?(): void,
	hasMore?: boolean,
	isLoadingMore?: boolean
}

// Sentinel value used to render the Load More button as a pseudo-option
const LOAD_MORE_VALUE = '__load_more__';

export default function SmartSearchWithLoadMore(props: IProps)
{
	const [inputValue, setInputValue] = React.useState('');
	const listboxRef = React.useRef<HTMLUListElement | null>(null);
	const scrollPositionRef = React.useRef<number>(0);

	React.useEffect(() =>
	{
		setInputValue(props.value?.text ?? '');
	}, [props.value?.text]);

	// Preserve scroll position when options change (e.g., after loading more)
	React.useEffect(() =>
	{
		if (listboxRef.current && scrollPositionRef.current > 0)
			listboxRef.current.scrollTop = scrollPositionRef.current;
	}, [props.options]);

	const onValueChange = (newValue: IOption | null) =>
	{
		// Ignore sentinel load more option selection
		if (newValue && newValue.value === LOAD_MORE_VALUE)
			return;
		setInputValue(newValue?.text ?? '');
		props.onValueChange(newValue);
	};

	const filterOptions = (options: IOption[]) => options;

	// Custom listbox component to capture ref for scroll position preservation
	const CustomListboxComponent = React.useMemo(
		() => React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(
			function CustomListbox(listboxProps, ref)
			{
				// Merge our ref with the forwarded ref
				const mergedRef = React.useCallback((node: HTMLUListElement | null) =>
				{
					listboxRef.current = node;
					if (typeof ref === 'function')
						ref(node);
					else if (ref)
						ref.current = node;
				}, [ref]);

				return <ul {...listboxProps} ref={mergedRef} />;
			}
		),
		[]
	);

	// Append sentinel option for Load More when more results available
	const displayedOptions = React.useMemo(() =>
	{
		if (props.hasMore)
			return [...props.options, { text: 'Load More', value: LOAD_MORE_VALUE, data: null } as IOption];
		return props.options;
	}, [props.hasMore, props.options]);

	const children = (
		<Autocomplete
			noOptionsText={(inputValue?.length ?? 0) === 0 ? 'Please enter search criteria' : 'No options found'}
			fullWidth={true}
			filterOptions={filterOptions}
			value={props.value}
			onChange={(_event, newValue) => onValueChange(newValue)}
			inputValue={inputValue}
			onInputChange={(_event, newInputValue, reason) =>
			{
				if (reason === 'input')
				{
					setInputValue(newInputValue);
					props.onInputChange(newInputValue);
				}
			}}
			isOptionEqualToValue={(option, value) => option.value === value.value}
			getOptionLabel={(option) => option.text}
			options={displayedOptions}
			ListboxComponent={CustomListboxComponent}
			renderOption={(renderProps, option) =>
			{
				if (option.value === LOAD_MORE_VALUE)
				{
					return (
						<li {...renderProps}>
							<Button
								variant="outlined"
								fullWidth
								size="small"
								disabled={props.isLoadingMore}
								onClick={(e) =>
								{
									e.preventDefault();
									e.stopPropagation();
									// Save scroll position before loading more
									if (listboxRef.current)
										scrollPositionRef.current = listboxRef.current.scrollTop;
									if (props.onLoadMore)
										props.onLoadMore();
								}}>
								{props.isLoadingMore ? <><CircularProgress size={16} sx={{ mr: 1 }} />Loading...</> : 'Load More'}
							</Button>
						</li>
					);
				}
				return <li {...renderProps}>{option.text}</li>;
			}}
			renderInput={(params) => (
				<StyledTextField
					{...params}
					data-testid="smartSearch"
					label="Search..."
					fullWidth={true}
					InputProps={{
						...params.InputProps,
						endAdornment: <>{props.isLoading && <InputAdornment position="end"><CircularProgress color="inherit" size={20} /></InputAdornment>} {params.InputProps.endAdornment}</>
					}}
				/>
			)}
		/>
	);

	return (
		<InputBase
			outerLabel={props.outerLabel}
			helpText={props.helpText}
			onValidatorResultChanged={props.onValidatorResultChanged}
			validator={props.validator}
			value={props.value}
			answerValue={props.answerValue}>
			{children}
		</InputBase>
	);
}
